import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Container, Link, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { wrapperSx } from '@/global/helpers/globalstyles';
import { apiGetRegionList } from '@/modules/regions/services/regions.service';
import PageHeader from '@/global/components/PageHeader';
import { SchoolsCatalogMain } from '@/modules/schools';

function CityAutoSchoolsPage() {
    const {cityId} = useParams<{ cityId: string }>();

    const {data: regions} = apiGetRegionList({
        limit: 100,
        offset: 0
    });

    const region = useMemo(() => {
        if (!regions) return null;
        return regions.items.find((region) => region.id === +(cityId ?? 0));
    }, [cityId, regions]);

    return (
        <Box
            className="App"
            sx={{height: '100%', display: 'flex', flexDirection: 'column'}}
        >
            <PageHeader/>
            <Helmet>
                <title>
                    Автошколы
                    {' '}
                    {region?.name_ru ?? ''}
                    {' '}
                    - Полный каталог автошкол города
                </title>
                <meta
                    name="description"
                    content={`Все автошколы ${region?.name_ru ?? ''}. Актуальные контакты, адреса, телефоны и категории обучения A, B, C, D. Выберите лучшую автошколу в ${region?.name_ru ?? ''} для получения водительских прав.`}
                />
                <meta
                    name="keywords"
                    content={`автошколы ${region?.name_ru ?? ''}, водительские права ${region?.name_ru ?? ''}, обучение вождению ${region?.name_ru ?? ''}, автошкола рядом ${region?.name_ru ?? ''}, категории A B C D ${region?.name_ru ?? ''}`}
                />
                <meta
                    property="og:title"
                    content={`Автошколы ${region?.name_ru ?? ''} - Каталог автошкол`}
                />
                <meta
                    property="og:description"
                    content={`Найдите лучшую автошколу в ${region?.name_ru ?? ''}. Полный список с контактами и категориями обучения.`}
                />
                <meta
                    property="og:type"
                    content="website"
                />
                <link
                    rel="canonical"
                    href={`${window.location.origin}/catalog`}
                />
            </Helmet>

            <Container
                maxWidth="xl"
                sx={wrapperSx}
            >
                <Box sx={{mb: 3}}>
                    <Link
                        component={RouterLink}
                        to="/catalog"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: '#007bff',
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        <ArrowBackIcon sx={{mr: 0.5, fontSize: '1rem'}}/>
                        Все автошколы Казахстана
                    </Link>
                </Box>

                <Box sx={{pb: 3}}>
                    <Typography
                        variant="h1"
                        component="h1"
                        sx={{
                            fontSize: {xs: '1.75rem', md: '2.25rem'},
                            fontWeight: 'bold',
                            color: '#2c3e50',
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        Автошколы
                        {' '}
                        {region?.name_ru}
                    </Typography>

                    <Typography
                        variant="h2"
                        component="p"
                        sx={{
                            fontSize: {xs: '1rem', md: '1.125rem'},
                            color: '#5a6c7d',
                            maxWidth: '800px',
                            lineHeight: 1.6
                        }}
                    >
                        Полный каталог автошкол города
                        {' '}
                        {region?.name_ru}
                        . Найдите автошколу рядом с домом,
                        сравните условия обучения и выберите подходящую для получения водительских прав.
                    </Typography>
                </Box>

                <SchoolsCatalogMain/>
            </Container>
        </Box>
    );
}

export default CityAutoSchoolsPage;
